import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { History, ChevronRight, Calendar as CalendarIcon, X, Share2, Camera, Loader2, CheckCircle2, ImageIcon, ZoomIn, Search } from "lucide-react";
import { format, startOfDay, endOfDay } from "date-fns";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import type { SessionsViewProps, SessionsFilter } from "@/ui/view-props";
import { FullScreenImageViewer } from "@/components/FullScreenImageViewer";

export function SessionsClassicView({
  sessions,
  loading,
  filter,
  setFilter,
  selectedDate,
  setSelectedDate,
  settings,
  isReadOnly,
  targetUserId,
  stats,
  onUploadScoreSheet,
  isUploading,
  scoreSheetUrls = {},
  searchQuery,
  setSearchQuery,
  }: SessionsViewProps) {
  const filters: { label: string; value: SessionsFilter }[] = [
    { label: "All", value: "all" },
    { label: "Unsettled", value: "unsettled" },
    { label: "Settled", value: "settled" },
  ];

  return (
    <div className="py-6 space-y-4">
      <h1 className="text-xl font-bold tracking-tight">Session History</h1>

      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold font-mono">{stats.unsettled}</p>
            <p className="text-xs text-muted-foreground mt-1">Unsettled</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold font-mono">{stats.totalSessions}</p>
            <p className="text-xs text-muted-foreground mt-1">Sessions</p>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold font-mono">{stats.players}</p>
            <p className="text-xs text-muted-foreground mt-1">Players</p>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          className="w-full pl-9 pr-4 py-2 text-sm bg-muted/50 rounded-lg border border-transparent focus:border-primary/40 outline-none transition-colors"
          placeholder="Search by player or date…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <div className="flex gap-2">
          {filters.map((f) => (
            <Button
              key={f.value}
              variant={filter === f.value ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {settings.session_calendar_search && (
          <div className="flex gap-2 ml-auto">
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant={selectedDate ? "default" : "outline"}
                  size="sm"
                  className={cn("gap-2", !selectedDate && "text-muted-foreground")}
                >
                  <CalendarIcon className="h-4 w-4" />
                  {selectedDate
                    ? format(selectedDate, "MMM d, yyyy")
                    : "Filter by date"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {selectedDate && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedDate(undefined)}
                className="px-2"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : sessions.length === 0 ? (
        <div className="text-center py-12 space-y-2">
          <History className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="text-muted-foreground">No sessions found.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {sessions.map((session) => (
            <Card key={session.id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
              <CardContent className="p-4 flex items-center justify-between">
                <Link
                  to={isReadOnly ? `/public/${targetUserId}/session/${session.id}` : `/session/${session.id}`}
                  className="flex-1 min-w-0"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <p className="font-semibold text-sm">
                    {format(new Date(session.date), "MMM d, yyyy")}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {session.session_players?.length || 0} players •{" "}
                    {format(new Date(session.date), "h:mm a")}
                  </p>
                </Link>
                <div className="flex items-center gap-2 shrink-0">
                  {scoreSheetUrls[session.id] && (
                    <FullScreenImageViewer 
                      src={scoreSheetUrls[session.id]} 
                      trigger={
                        <Button size="icon" variant="ghost" className="h-7 w-7 p-0 overflow-hidden border border-border">
                          <img 
                            src={scoreSheetUrls[session.id]} 
                            alt="thumbnail" 
                            className="h-full w-full object-cover"
                          />
                        </Button>
                      }
                    />
                  )}
                  <Badge variant={session.status === "settled" ? "secondary" : "default"}>
                    {session.status}
                  </Badge>
                  {!isReadOnly && (
                    <div className="flex items-center">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        id={`camera-${session.id}`}
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) onUploadScoreSheet(session.id, file);
                        }}
                      />
                      <input
                        type="file"
                        accept="image/*"
                        id={`gallery-${session.id}`}
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) onUploadScoreSheet(session.id, file);
                        }}
                      />
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-7 w-7"
                            disabled={isUploading === session.id}
                          >
                            {isUploading === session.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (session.score_sheet_path) ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                            ) : (
                              <Camera className="h-3.5 w-3.5" />
                            )}
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => document.getElementById(`camera-${session.id}`)?.click()}>
                            <Camera className="mr-2 h-4 w-4" />
                            <span>Take Photo</span>
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => document.getElementById(`gallery-${session.id}`)?.click()}>
                            <ImageIcon className="mr-2 h-4 w-4" />
                            <span>Photo Library</span>
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    title="Share on WhatsApp"
                    onClick={(e) => {
                      e.preventDefault();
                      const dateStr = format(new Date(session.date), "MMM d, yyyy");
                      const resultsSummary = [...(session.session_players || [])]
                        .sort((a, b) => (b.net_amount || 0) - (a.net_amount || 0))
                        .map((sp, i) => `${i + 1}. ${sp.players?.name}: ${sp.net_amount >= 0 ? '+' : ''}${Math.round(sp.net_amount)}`)
                        .join('\n');
                      
                      const prefix = isReadOnly ? `/public/${targetUserId}` : '';
                      const url = `${window.location.origin}${prefix}/session/${session.id}`;
                      const text = `🃏 SSC Session — ${dateStr}\n\n${resultsSummary}\n\nFull results & portal: ${url}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                    }}
                  >
                    <Share2 className="h-3.5 w-3.5" />
                  </Button>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

